import type { Popup } from 'popups/Popup';
import { Signal } from 'typed-signals';
import type { Bounds, Container } from 'pixi.js';
import { getLocalizedValue } from '@softgames/localization';
import { displayElementsInRows } from 'game/utils/displayElementsInRows';
import { FailPopupLayout } from '../../../../../layouts/popups/FailPopupLayout';
import { GAME_ECONOMY_SETTINGS } from '../../../GameConstants';
import type { PopupElementAnimationData } from '../../../../popups/PopupAnimations';
import type { LevelTaskProgress } from '../../../tasks/types';
import { centerElements } from '../../../utils/centerElements';
import { TaskToCollectView } from '../../common/TaskToCollectView';
import { playLayoutAnimation } from '../../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { applyLayoutState } from '../../../../libs/layouts/populateLayouts/applyLayoutState';
import type { IAPProduct } from '../../../../app/iap/types';
import { IAPProductId } from '../../../../app/iap/types';
import { ShopOfferView } from '../../../../mainMenu/shopPage/ShopOfferView';
import type { AppStateController } from '../../../../app/state/AppStateController';

export interface FailPopupData {
  readonly state: AppStateController;
  readonly tasks: LevelTaskProgress[];
  readonly unlimitedLives: boolean;
  readonly lossProgressBadges: Container[];
  readonly layoutType: FailPopupLayoutType;
}

export enum FailPopupLayoutType {
  Default = 'Default',
  AdRevive = 'AdRevive',
  Retry = 'Retry',
}

export enum FailPopupStep {
  OutOfSpace = 'OutOfSpace',
  Confirmation = 'Confirmation',
  LifeLost = 'LifeLost',
}

export class FailPopup implements Popup {
  public readonly onPlayButtonClicked = new Signal();

  public readonly onRetryButtonClicked = new Signal();

  public readonly onQuitButtonClicked = new Signal<(step: FailPopupStep) => void>();

  public readonly onOfferClicked = new Signal<(offer: IAPProduct) => void>();

  public readonly onAdButtonClicked = new Signal();

  public readonly onCloseButtonClicked = new Signal<(step: FailPopupStep) => void>();

  public readonly root: Container;

  public initialBounds: Bounds;

  private readonly layout: FailPopupLayout;

  public readonly onEvent = new Signal<() => void>();

  private currentStep = FailPopupStep.OutOfSpace;

  constructor(private readonly data: FailPopupData) {
    this.layout = new FailPopupLayout();
    this.root = this.layout.root;
    this.initialBounds = this.root.getLocalBounds();

    this.initialBounds.maxY += 300;

    this.init();

    // Applied after init(): handlePopupState() runs applyLayoutState, which pins
    // popup.y per layout state (e.g. the AdRevive 'adButton' state), so offsetting
    // before init() gets overwritten. Nudge the popup down to clear the top coin counter.
    this.layout.popup.y += 60;
  }

  private init(): void {
    const { closeButton, playButton, retryButton, quitButton, adButton } = this.layout;

    playButton.on('pointertap', () => {
      this.onPlayButtonClicked.emit();
    });

    closeButton.on('pointertap', () => {
      this.onCloseButtonClicked.emit(this.currentStep);
    });

    retryButton.on('pointertap', () => {
      this.onRetryButtonClicked.emit();
    });

    quitButton.on('pointertap', () => {
      this.onQuitButtonClicked.emit(this.currentStep);
    });

    adButton.on('pointertap', () => {
      this.onAdButtonClicked.emit();
    });

    this.initViews();

    this.handlePopupState();
  }

  private initViews(): void {
    const {
      priceText,
      lifeLostLayout: { currentLivesText, finalLivesText },
    } = this.layout;
    const { reviveCoinCost } = GAME_ECONOMY_SETTINGS;

    priceText.text = getLocalizedValue(reviveCoinCost);

    currentLivesText.text = getLocalizedValue(this.data.state.getLives());
    finalLivesText.text = getLocalizedValue(this.data.state.getLives() - 1);

    this.createTasksViews(this.data.tasks);
    this.createProgressIconViews();
  }

  private handlePopupState(): void {
    const { outOfSpaceLayout, areYouSureLayout, lifeLostLayout } = this.layout;

    const { unlimitedLives } = this.data;

    const hasProgressToLose = this.hasProgressToLose();

    switch (this.data.layoutType) {
      case FailPopupLayoutType.AdRevive:
        applyLayoutState(this.layout, 'adButton');
        applyLayoutState(outOfSpaceLayout, 'watchAd');
        applyLayoutState(areYouSureLayout, hasProgressToLose ? 'goalsAd' : 'watchAd');
        applyLayoutState(lifeLostLayout, 'watchAd');
        break;
      case FailPopupLayoutType.Retry:
        applyLayoutState(this.layout, unlimitedLives && !hasProgressToLose ? 'retryFlowUnlimitedLives' : 'retryFlow');
        if (hasProgressToLose) applyLayoutState(areYouSureLayout, 'goals');
        break;
      case FailPopupLayoutType.Default:
      default:
        if (hasProgressToLose) applyLayoutState(areYouSureLayout, 'goals');
        break;
    }
  }

  public async advancePopupStep(): Promise<boolean> {
    const {
      layout: {
        outOfSpaceLayout,
        areYouSureLayout,
        closeButton,
        lifeLostLayout,
        adButton,
        playButton,
        quitButton,
        retryButton,
      },
    } = this;

    let hasAdvanced = false;

    closeButton.eventMode = 'none';
    if (this.currentStep === FailPopupStep.OutOfSpace) {
      if (this.data.unlimitedLives && !this.hasProgressToLose()) {
        const promises: Promise<void>[] = [];
        promises.push(playLayoutAnimation(this.layout, 'switchToAreYouSure_UnlimitedLives'));
        promises.push(playLayoutAnimation(outOfSpaceLayout, 'switchToAreYouSure_UnlimitedLives'));
        await Promise.all(promises);
      } else {
        await playLayoutAnimation(this.layout, 'switchToAreYouSure');
        void playLayoutAnimation(areYouSureLayout, 'Idle', true);
      }
      this.currentStep = FailPopupStep.Confirmation;
      hasAdvanced = true;
    } else if (this.currentStep === FailPopupStep.Confirmation) {
      const eventCallback = async (event: string): Promise<void> => {
        if (event === 'playHeartAnimation') {
          await playLayoutAnimation(lifeLostLayout, 'Lose_Life');
          void playLayoutAnimation(lifeLostLayout, 'Idle_After_Losing_Life', true);
        } else if (event === 'updateButtons') {
          closeButton.props({
            texture: 'Home_Blue_Button',
          });
        }
        adButton.props({
          visible: false,
        });

        playButton.props({
          visible: false,
        });

        quitButton.props({
          visible: false,
        });

        retryButton.props({
          visible: true,
        });
      };

      if (this.data.unlimitedLives) {
        void playLayoutAnimation(lifeLostLayout, 'Infinite_Life_Case', true);
        await playLayoutAnimation(this.layout, 'switchToLifeLost_UnlimitedLives', false, eventCallback);
      } else {
        await playLayoutAnimation(this.layout, 'switchToLifeLost', false, eventCallback);
      }
      this.currentStep = FailPopupStep.LifeLost;

      hasAdvanced = true;
    }

    closeButton.eventMode = 'static';
    return hasAdvanced;
  }

  private hasProgressToLose(): boolean {
    const { lossProgressBadges } = this.data;
    return lossProgressBadges.length > 0;
  }

  private createTasksViews(tasks: LevelTaskProgress[]): void {
    const { outOfSpaceLayout } = this.layout;

    const tasksViews = tasks.map((task) =>
      new TaskToCollectView(task.type, task.goal - task.collected).props({
        parent: outOfSpaceLayout.tasksSlot,
      }),
    );
    centerElements(tasksViews, 40);
  }

  private createProgressIconViews(): void {
    const {
      areYouSureLayout: { goalsSlot },
    } = this.layout;

    const icons: Container[] = [];

    const { lossProgressBadges } = this.data;

    lossProgressBadges.forEach((icon) => {
      icon.props({
        parent: goalsSlot,
      });
      icons.push(icon);
    });

    const hasAdButton = this.data.layoutType === FailPopupLayoutType.AdRevive;

    displayElementsInRows(icons, {
      containerWidth: 300,
      containerHeight: hasAdButton ? 120 : 140,
      horizontalDistance: 50,
      maxElementsInRow: 3,
      minScale: 0.5,
      maxScale: 1.3,
    });
  }

  public getAnimatedElements(phase: 'in' | 'out'): PopupElementAnimationData[] {
    const { layout } = this;
    const animationData: PopupElementAnimationData[] = [];
    if (phase === 'in') {
      animationData.push(
        { container: layout.popup, scaleEase: 'backOut', duration: 400 },
        { container: layout.closeButton, delay: 50, scaleEase: 'backOut', duration: 400 },
        { container: layout.playButton, delay: 20 },
        { container: layout.adButton, delay: 20 },
      );
    } else {
      animationData.push({ container: layout.popup });
    }
    return animationData;
  }

  public addBannerOffer(offer: IAPProduct): void {
    if (this.root.destroyed) return;

    const offerView = new ShopOfferView(offer).props({
      parent: this.layout.offerSlot,
      alpha: 0,
    });

    offerView.onOfferClicked.connect(() => {
      if (!offer) return;
      this.onOfferClicked.emit(offer);
    });

    if (offer.id === (IAPProductId.SafetyNet as string)) {
      offerView.legacyShowValueRibbon();
    }

    offerView.tweenTo({ alpha: 1 }, 200, 'sinOut', true);
  }
}
